import React, { useRef, FC } from 'react';
import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import { patchUrlByLocale } from 'utils';
import { HeaderModelData } from 'types/model';
import { Divider } from 'fronton-react';
import { useTranslation } from 'react-i18next';
import {
  MagnifyingGlassIcon,
  BookmarkSimpleIcon,
  ShoppingCartSimpleIcon,
  IconComponent,
} from '@fronton/icons-react';
import { useMobileMenuAnalyticsEvent } from 'components/header/hooks';
import { NAStore } from 'components/icons/nastore';
import { getIsB2BEnabled } from 'selectors';
import { MobileMenuLink } from './mobile-menu-link';
import { MobileFooterClassName, root, dividerFix } from './styles';
import { matchRoute } from './utils';
import { BasketMenuLink } from './basket-menu-link';
import { MobileUserMenu } from './mobile-user-menu';
import { ShoppingListMenuMobile } from '../shopping-list';
import { useAllowComparison } from 'common/hooks/use-allow-comparison';
import { useMobileBottomPadding } from '../header-layout/hooks/use-mobile-bottom-padding';

export const QA_LOCATOR = 'mobile-fixed-menu';

export type MenuMobileProps = {
  model: HeaderModelData;
};

export const MenuMobile: FC<MenuMobileProps> = ({ model }) => {
  const menuMobileRef = useRef<HTMLDivElement>(null);

  const { home, shoppingList, shops, initUrl, linkToCompare } = model.header;
  const {
    cookies: { lang },
  } = useStoreon<State>('cookies');

  const { pathname } = global?.location || { pathname: '' };

  const { t } = useTranslation();

  const getIsActive = (href: string) => matchRoute(pathname, href);

  const onMainPageLinkClick = useMobileMenuAnalyticsEvent('Главная');
  const onShoppingListLinkClick = useMobileMenuAnalyticsEvent('Мой список');
  const onShopsLinkClick = useMobileMenuAnalyticsEvent('Магазины');

  const isB2BEnabled = getIsB2BEnabled(model.experiments);

  const isAllowComparison = useAllowComparison();

  useMobileBottomPadding(menuMobileRef);

  return (
    <div className={MobileFooterClassName} data-qa={QA_LOCATOR} ref={menuMobileRef}>
      <Divider className={dividerFix} />
      <div className={root}>
        <MobileMenuLink
          icon={MagnifyingGlassIcon}
          href={patchUrlByLocale({ url: home, pathname, lang })}
          text={t('header.main.main-page')}
          getIsActive={getIsActive}
          data-qa="mobile-menu-home-link"
          onClick={onMainPageLinkClick}
        />

        {isAllowComparison ? (
          <ShoppingListMenuMobile
            shoppingListHref={patchUrlByLocale({ url: shoppingList, pathname, lang })}
            comparisonListHref={patchUrlByLocale({ url: linkToCompare, pathname, lang })}
          />
        ) : (
          <MobileMenuLink
            icon={BookmarkSimpleIcon}
            href={patchUrlByLocale({ url: shoppingList, pathname, lang })}
            text={t('header.main.shopping-list')}
            getIsActive={getIsActive}
            data-qa="mobile-menu-shopping-list-link"
            onClick={onShoppingListLinkClick}
          />
        )}

        <MobileMenuLink
          customIcon={NAStore as IconComponent}
          href={patchUrlByLocale({ url: shops, pathname, lang })}
          text={t('header.main.stores')}
          getIsActive={getIsActive}
          data-qa="mobile-menu-shops-link"
          onClick={onShopsLinkClick}
        />

        <MobileUserMenu
          href={initUrl}
          shoppingListUrl={patchUrlByLocale({ url: shoppingList, pathname, lang })}
          isB2BEnabled={isB2BEnabled}
        />

        <BasketMenuLink
          icon={ShoppingCartSimpleIcon}
          href={patchUrlByLocale({ url: '/basket/', pathname, lang })}
          text={t('header.main.basket')}
          getIsActive={getIsActive}
          data-qa="mobile-menu-basket-link"
        />
      </div>
    </div>
  );
};
